import React, { useCallback, useEffect, useRef, useState } from 'react';

const AUDIO_PROGRESS_STYLE_ID = 'plynote-audio-progress-style';
if (typeof document !== 'undefined' && !document.getElementById(AUDIO_PROGRESS_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = AUDIO_PROGRESS_STYLE_ID;
  style.textContent = `
    .audio-block-progress {
      -webkit-appearance: none; appearance: none;
      position: absolute; top: 0; left: 0;
      width: 100%; height: 100%;
      margin: 0; padding: 0; border: none; outline: none;
      background: transparent; cursor: pointer; z-index: 10;
    }
    .audio-block-progress:disabled { cursor: not-allowed; opacity: 0.55; }
    .audio-block-progress:focus-visible { outline: none; box-shadow: none; }
    .audio-block-progress::-webkit-slider-runnable-track {
      height: 7px; border-radius: 999px; background: transparent;
    }
    .audio-block-progress::-webkit-slider-thumb {
      -webkit-appearance: none; appearance: none;
      width: 14px; height: 14px; margin-top: -3.5px;
      border-radius: 50%; border: 1.5px solid rgba(255,255,255,0.8);
      background: rgba(226,232,240,0.94); cursor: pointer;
      transition: transform 180ms ease;
    }
    .audio-block-progress:active::-webkit-slider-thumb { transform: scale(1.1); }
    .audio-block-progress::-moz-range-track {
      height: 7px; border-radius: 999px; background: transparent;
    }
    .audio-block-progress::-moz-range-thumb {
      width: 14px; height: 14px; border-radius: 50%;
      border: 1.5px solid rgba(255,255,255,0.8);
      background: rgba(226,232,240,0.94); cursor: pointer;
      transition: transform 180ms ease;
    }
    .audio-block-progress:active::-moz-range-thumb { transform: scale(1.1); }
  `;
  document.head.appendChild(style);
}

const hexToRgba = (hex, alpha) => {
  const safe = /^#([A-Fa-f0-9]{6})$/.test(String(hex || '')) ? hex : '#000000';
  const r = parseInt(safe.slice(1, 3), 16);
  const g = parseInt(safe.slice(3, 5), 16);
  const b = parseInt(safe.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const fmt = (s) => {
  const n = Math.max(0, Math.floor(Number(s) || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};

const AudioBlock = React.forwardRef(({
  audioSrc = '',
  audioBlockItem = null,
  audioName = 'Untitled Audio',
  totalDurationSeconds: durationProp = null,
  color = '#3B82F6',
  size = 'md',
  width = '100%',
  height = 'auto',
  backgroundColor = '#1A1A1A',
  audioPartBackgroundColor = null,
  borderColor = '#2A2A2A',
  textColor = '#E0E0E0',
  accentColor = '#9E9E9E',
  padding = '16px',
  borderRadius = '12px',
  disabled = false,
  className = '',
  onPlayStart = null,
  onPlayStateChange = null,
  onTimeUpdate = null,
}, ref) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [rawCurrentTime, setRawCurrentTime] = useState(0);
  const [nativeDuration, setNativeDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekPreviewPercent, setSeekPreviewPercent] = useState(null);

  // Smooth RAF-based progress
  const [smoothTime, setSmoothTime] = useState(0);
  const sampleRef = useRef({ t: 0, ts: 0 });
  const rafRef = useRef(null);

  const totalDuration = Math.max(
    0,
    nativeDuration > 0 ? nativeDuration :
    (Number.isFinite(Number(durationProp)) && Number(durationProp) > 0 ? Number(durationProp) :
    (Number.isFinite(Number(audioBlockItem?.durationSeconds)) && Number(audioBlockItem?.durationSeconds) > 0
      ? Number(audioBlockItem?.durationSeconds) : 0))
  );

  // Keep RAF sample in sync with real audio time
  useEffect(() => {
    sampleRef.current = { t: rawCurrentTime, ts: performance.now() };
    if (!isPlaying || isSeeking) setSmoothTime(rawCurrentTime);
  }, [rawCurrentTime, isPlaying, isSeeking]);

  // RAF loop
  useEffect(() => {
    if (!isPlaying || isSeeking || totalDuration <= 0) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    const tick = () => {
      const elapsed = Math.max(0, (performance.now() - sampleRef.current.ts) / 1000);
      setSmoothTime(Math.min(totalDuration, sampleRef.current.t + elapsed));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, isSeeking, totalDuration]);

  // Expose pause() to parent (for multi-block coordination)
  React.useImperativeHandle(ref, () => ({
    pause: () => audioRef.current?.pause(),
  }), []);

  // ── Toggle ────────────────────────────────────────────────────────────────
  const handleToggle = useCallback(async () => {
    const el = audioRef.current;
    if (!el || disabled) return;

    if (!el.paused && !el.ended) {
      el.pause();
      return;
    }

    onPlayStart?.();
    try {
      if (el.readyState === 0) el.load();
      await el.play();
    } catch {}
  }, [disabled, onPlayStart]);

  // ── Seek ─────────────────────────────────────────────────────────────────
  const commitSeekPercent = useCallback((pct) => {
    const el = audioRef.current;
    const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
    if (el && totalDuration > 0) {
      const nextT = (clamped / 100) * totalDuration;
      el.currentTime = nextT;
      sampleRef.current = { t: nextT, ts: performance.now() };
      setSmoothTime(nextT);
    }
    setIsSeeking(false);
    setSeekPreviewPercent(null);
  }, [totalDuration]);

  const handleSeekInput = (e) => {
    if (!totalDuration) return;
    setIsSeeking(true);
    setSeekPreviewPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0)));
  };

  const handleSeekStart = (e) => {
    if (!totalDuration) return;
    setIsSeeking(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };

  const commitSeek = (e) => {
    const v = Number(e?.target?.value);
    commitSeekPercent(Number.isFinite(v) ? v : (seekPreviewPercent ?? displayedPct));
  };

  useEffect(() => {
    if (!isSeeking) return;
    const fn = () => commitSeekPercent(seekPreviewPercent ?? displayedPct);
    window.addEventListener('pointerup', fn);
    window.addEventListener('pointercancel', fn);
    return () => {
      window.removeEventListener('pointerup', fn);
      window.removeEventListener('pointercancel', fn);
    };
  }, [isSeeking, seekPreviewPercent, commitSeekPercent]); // eslint-disable-line

  // ── Display values ────────────────────────────────────────────────────────
  const displayTime = isSeeking
    ? ((seekPreviewPercent ?? 0) / 100) * totalDuration
    : Math.min(totalDuration || Infinity, smoothTime);
  const progressPct = totalDuration > 0
    ? Math.max(0, Math.min(100, (displayTime / totalDuration) * 100))
    : 0;
  const displayedPct = seekPreviewPercent ?? progressPct;

  const resolvedName = String(audioBlockItem?.title || audioName || 'Untitled Audio');
  const isDisabled = disabled || !audioSrc;

  // ── Styles ────────────────────────────────────────────────────────────────
  const outerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width,
    height,
    padding,
    background: backgroundColor === 'transparent' ? 'transparent' : backgroundColor,
    borderRadius,
    fontFamily: 'Sora, Poppins, Inter, sans-serif',
    opacity: isDisabled ? 0.6 : 1,
    transition: 'opacity 0.3s ease',
    border: !borderColor || borderColor === 'transparent' ? 'none' : `1px solid ${hexToRgba(borderColor, 0.78)}`,
    position: 'relative',
  };

  const controlsBarStyle = {
    display: 'flex',
    flexDirection: 'column',
    background: audioPartBackgroundColor || hexToRgba('#000000', 0.12),
    borderRadius: 14,
    padding: '10px 14px 12px',
    border: `1px solid ${hexToRgba(color, 0.42)}`,
    transition: 'border-color 0.3s ease',
    marginTop: 2,
  };

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: size === 'sm' ? 48 : size === 'lg' ? 80 : 64,
  };

  return (
    <div style={outerStyle} className={className}>
      {/* Hidden audio element — fully managed by this component */}
      <audio
        ref={audioRef}
        src={audioSrc || undefined}
        onPlay={() => { setIsPlaying(true); onPlayStateChange?.(true); }}
        onPause={() => { setIsPlaying(false); onPlayStateChange?.(false); }}
        onEnded={() => { setIsPlaying(false); onPlayStateChange?.(false); }}
        onTimeUpdate={(e) => {
          const t = Number(e.currentTarget.currentTime) || 0;
          setRawCurrentTime(t);
          onTimeUpdate?.(t);
        }}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setNativeDuration(d);
        }}
        preload="metadata"
        style={{ display: 'none' }}
      />

      {/* Controls bar */}
      <div style={controlsBarStyle}>
        <div style={rowStyle}>
          {/* Title */}
          <div style={{
            flex: 1,
            fontSize: size === 'sm' ? 14 : size === 'lg' ? 18 : 16,
            fontWeight: size === 'sm' ? 500 : 600,
            color: hexToRgba(textColor, 0.98),
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            letterSpacing: '0.01em',
            minWidth: 0,
          }} title={resolvedName}>
            {resolvedName}
          </div>

          {/* Time */}
          <div style={{
            fontSize: size === 'sm' ? 12 : 14,
            fontWeight: 600,
            color: hexToRgba(accentColor, 0.95),
            whiteSpace: 'nowrap',
            letterSpacing: '0.04em',
            flexShrink: 0,
          }}>
            {fmt(displayTime)} / {totalDuration > 0 ? fmt(totalDuration) : '0:00'}
          </div>

          {/* Play / Pause button — native, no wrapper */}
          <button
            type="button"
            onClick={handleToggle}
            disabled={isDisabled}
            aria-label={isPlaying ? `Pause ${resolvedName}` : `Play ${resolvedName}`}
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: size === 'sm' ? 30 : size === 'lg' ? 42 : 36,
              height: size === 'sm' ? 30 : size === 'lg' ? 42 : 36,
              borderRadius: '50%',
              border: `1px solid ${hexToRgba(color, 0.35)}`,
              background: hexToRgba(color, 0.12),
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
              outline: 'none',
              transition: 'background 0.2s ease, border-color 0.2s ease',
              padding: 0,
            }}
          >
            <svg
              width={size === 'sm' ? 16 : size === 'lg' ? 22 : 18}
              height={size === 'sm' ? 16 : size === 'lg' ? 22 : 18}
              viewBox="0 0 24 24"
              fill="none"
            >
              {isPlaying ? (
                <>
                  <rect x="6" y="4" width="4" height="16" rx="1.5" fill={hexToRgba(color, 0.95)} />
                  <rect x="14" y="4" width="4" height="16" rx="1.5" fill={hexToRgba(color, 0.95)} />
                </>
              ) : (
                <path d="M7 4.5L20 12L7 19.5V4.5Z" fill={hexToRgba(color, 0.95)} />
              )}
            </svg>
          </button>

          {/* Download button */}
          {audioSrc && (
            <button
              type="button"
              aria-label={`Download ${resolvedName}`}
              onClick={async () => {
                try {
                  const res = await fetch(audioSrc);
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${resolvedName}.mp3`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch {}
              }}
              style={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: size === 'sm' ? 30 : size === 'lg' ? 42 : 36,
                height: size === 'sm' ? 30 : size === 'lg' ? 42 : 36,
                borderRadius: '50%',
                border: `1px solid ${hexToRgba(accentColor, 0.25)}`,
                background: hexToRgba(accentColor, 0.07),
                cursor: 'pointer',
                outline: 'none',
                transition: 'background 0.2s ease, border-color 0.2s ease',
                padding: 0,
              }}
            >
              <svg
                width={size === 'sm' ? 14 : size === 'lg' ? 20 : 16}
                height={size === 'sm' ? 14 : size === 'lg' ? 20 : 16}
                viewBox="0 0 24 24"
                fill="none"
              >
                <path d="M12 3v12m0 0l-4-4m4 4l4-4" stroke={hexToRgba(accentColor, 0.8)} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke={hexToRgba(accentColor, 0.8)} strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div style={{ width: '100%', marginTop: 8 }}>
          <div style={{
            position: 'relative',
            width: '100%',
            height: 7,
            borderRadius: 999,
            background: hexToRgba('#000000', 0.22),
            border: `1px solid ${hexToRgba(color, 0.18)}`,
            overflow: 'hidden',
          }}>
            {/* Fill */}
            <div style={{
              position: 'absolute',
              top: 0, left: 0,
              width: `${displayedPct}%`,
              height: '100%',
              background: hexToRgba(color, 0.72),
              borderRadius: 999,
              pointerEvents: 'none',
            }} />
            {/* Range input */}
            <input
              className="audio-block-progress"
              type="range"
              min={0}
              max={100}
              step={0.1}
              value={displayedPct}
              onInput={handleSeekInput}
              onChange={handleSeekInput}
              onPointerDown={handleSeekStart}
              onPointerUp={commitSeek}
              onPointerCancel={commitSeek}
              onKeyUp={commitSeek}
              onBlur={commitSeek}
              disabled={isDisabled || totalDuration <= 0}
              aria-label="Audio playback position"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

AudioBlock.displayName = 'AudioBlock';
export default AudioBlock;
